/**
 * Platform Admin Dashboard - TradeZella Inspired
 * Rich analytics, charts, and performance metrics
 * 
 * Created: 2026-01-06
 * Version: 30.2
 */

import React, { useState, useEffect, useMemo } from 'react';
import Navigation from '../components/Navigation';
import { 
    Users, Activity, TrendingUp, TrendingDown, DollarSign,
    BarChart3, Shield, Download, Search, RefreshCw, Eye, Ban,
    CheckCircle, XCircle, Bot, Database, Trophy, Play, Square,
    Calendar, Target, Zap, Award, Clock, PieChart, ArrowUp, ArrowDown,
    ChevronRight, ChevronDown, Filter, MoreHorizontal,
    Plus, Trash2, Save, Edit2, Lock, KeyRound, Ticket, Copy, Link2
} from 'lucide-react';
import {
    LineChart, Line, AreaChart, Area, BarChart, Bar, 
    PieChart as RechartsPie, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    Legend, ComposedChart, Scatter, ScatterChart
} from 'recharts';
import toast from 'react-hot-toast';
import AdminEmulate from '../components/AdminEmulate';

// API helper
const adminAPI = {
    getStats: () => fetch('/api/platform-admin/stats', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),
    
    getUsers: (includeInactive = false) => fetch(`/api/platform-admin/users?includeInactive=${includeInactive}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),
    
    getUserDetails: (id) => fetch(`/api/platform-admin/users/${id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),
    
    toggleUser: (id) => fetch(`/api/platform-admin/users/${id}/toggle`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),
    
    getTrades: (params = {}) => {
        const query = new URLSearchParams(params).toString();
        return fetch(`/api/platform-admin/trades?${query}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(r => r.json());
    },
    
    getLogs: (params = {}) => {
        const query = new URLSearchParams(params).toString();
        return fetch(`/api/platform-admin/logs?${query}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }).then(r => r.json());
    },
    
    getBotActivity: (limit = 100) => fetch(`/api/platform-admin/bot-activity?limit=${limit}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),

    getBots: () => fetch('/api/platform-admin/bots', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),
    
    startBot: (userId) => fetch(`/api/platform-admin/bots/${userId}/start`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),
    
    stopBot: (userId) => fetch(`/api/platform-admin/bots/${userId}/stop`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),
    
    startAllBots: () => fetch('/api/platform-admin/bots/start-all', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),
    
    stopAllBots: () => fetch('/api/platform-admin/bots/stop-all', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),

    // ---- RBAC: roles & permissions ----
    getPermissions: () => fetch('/api/platform-admin/permissions', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),

    getRoles: () => fetch('/api/platform-admin/roles', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),

    createRole: (body) => fetch('/api/platform-admin/roles', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
    }).then(r => r.json()),

    updateRole: (id, body) => fetch(`/api/platform-admin/roles/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
    }).then(r => r.json()),

    deleteRole: (id) => fetch(`/api/platform-admin/roles/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),

    assignRole: (userId, role) => fetch(`/api/platform-admin/users/${userId}/role`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ role })
    }).then(r => r.json()),

    // ---- Invites ----
    getInvites: () => fetch('/api/platform-admin/invites', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),

    createInvite: (body) => fetch('/api/platform-admin/invites', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
    }).then(r => r.json()),

    revokeInvite: (token) => fetch(`/api/platform-admin/invites/${encodeURIComponent(token)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),
};

// Colors
const COLORS = {
    green: '#10b981',
    red: '#ef4444',
    blue: '#3b82f6',
    purple: '#8b5cf6',
    yellow: '#f59e0b',
    cyan: '#06b6d4',
    pink: '#ec4899',
    gray: '#6b7280',
};

// ============ PAERU SCORE COMPONENT ============
const PaeruScore = ({ score, size = 'large' }) => {
    const radius = size === 'large' ? 70 : 40;
    const stroke = size === 'large' ? 10 : 6;
    const circumference = 2 * Math.PI * radius;
    const progress = (score / 100) * circumference;
    
    const getScoreColor = (s) => {
        if (s >= 80) return COLORS.green;
        if (s >= 60) return COLORS.blue;
        if (s >= 40) return COLORS.yellow;
        return COLORS.red;
    };
    
    const getScoreLabel = (s) => {
        if (s >= 80) return 'Excellent';
        if (s >= 60) return 'Good';
        if (s >= 40) return 'Average';
        return 'Needs Work';
    };

    return (
        <div className="flex flex-col items-center">
            <div className="relative">
                <svg width={(radius + stroke) * 2} height={(radius + stroke) * 2}>
                    <circle
                        cx={radius + stroke}
                        cy={radius + stroke}
                        r={radius}
                        fill="none"
                        stroke="rgba(255,255,255,0.1)"
                        strokeWidth={stroke}
                    />
                    <circle
                        cx={radius + stroke}
                        cy={radius + stroke}
                        r={radius}
                        fill="none"
                        stroke={getScoreColor(score)}
                        strokeWidth={stroke}
                        strokeDasharray={circumference}
                        strokeDashoffset={circumference - progress}
                        strokeLinecap="round"
                        transform={`rotate(-90 ${radius + stroke} ${radius + stroke})`}
                        style={{ transition: 'stroke-dashoffset 1s ease-out' }}
                    />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className={`font-bold ${size === 'large' ? 'text-3xl' : 'text-xl'}`} style={{ color: getScoreColor(score) }}>
                        {score}
                    </span>
                    {size === 'large' && <span className="text-xs text-text-muted">/ 100</span>}
                </div>
            </div>
            {size === 'large' && (
                <span className="mt-2 text-sm font-medium" style={{ color: getScoreColor(score) }}>
                    {getScoreLabel(score)}
                </span>
            )}
        </div>
    );
};

// ============ STAT CARD COMPONENT ============
const StatCard = ({ title, value, change, changeLabel, icon: Icon, color = 'blue', subtitle }) => (
    <div className="card p-4 hover:border-accent-purple/30 transition-all">
        <div className="flex items-start justify-between mb-3">
            <div className={`w-10 h-10 rounded-xl bg-accent-${color}/15 flex items-center justify-center`}>
                <Icon className={`w-5 h-5 text-accent-${color}`} />
            </div>
            {change !== undefined && (
                <div className={`flex items-center gap-1 text-xs ${change >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                    {change >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                    {Math.abs(change)}%
                </div>
            )}
        </div>
        <p className="text-text-muted text-sm mb-1">{title}</p>
        <p className="text-2xl font-bold">{value}</p>
        {subtitle && <p className="text-text-muted text-xs mt-1">{subtitle}</p>}
        {changeLabel && <p className="text-text-muted text-xs mt-1">{changeLabel}</p>}
    </div>
);

// ============ CALENDAR HEATMAP ============
const CalendarHeatmap = ({ trades }) => {
    const today = new Date();
    const days = [];
    
    for (let i = 34; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        const dayTrades = trades.filter(t => t.timestamp?.startsWith(dateStr));
        const pnl = dayTrades.reduce((sum, t) => sum + (parseFloat(t.pnl) || 0), 0);
        
        days.push({
            date: dateStr,
            day: date.getDate(),
            dayOfWeek: date.getDay(),
            trades: dayTrades.length,
            pnl,
        });
    }
    
    const getColor = (pnl, trades) => {
        if (trades === 0) return 'bg-bg-tertiary';
        if (pnl > 100) return 'bg-accent-green';
        if (pnl > 0) return 'bg-accent-green/50';
        if (pnl > -100) return 'bg-accent-red/50';
        return 'bg-accent-red';
    };

    return (
        <div className="card p-4">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-accent-blue" />
                Trading Activity (Last 35 Days)
            </h3>
            <div className="grid grid-cols-7 gap-1">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                    <div key={i} className="text-center text-xs text-text-muted py-1">{d}</div>
                ))}
                {days.map((day, i) => (
                    <div 
                        key={i}
                        className={`aspect-square rounded-sm ${getColor(day.pnl, day.trades)} flex items-center justify-center text-xs cursor-pointer hover:ring-2 hover:ring-white/30 transition-all`}
                        title={`${day.date}: ${day.trades} trades, $${day.pnl.toFixed(2)} P&L`}
                    >
                        {day.trades > 0 && <span className="text-white font-medium">{day.trades}</span>}
                    </div>
                ))}
            </div>
            <div className="flex items-center justify-end gap-4 mt-4 text-xs text-text-muted">
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-sm bg-accent-red"></div>
                    <span>Loss</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-sm bg-bg-tertiary"></div>
                    <span>No trades</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded-sm bg-accent-green"></div>
                    <span>Profit</span>
                </div>
            </div>
        </div>
    );
};

// ============ USER PERFORMANCE ROW ============
const UserPerformanceRow = ({ user, rank, onViewDetails, onEmulate }) => {
    const pnl = parseFloat(user.stats?.totalPnL || 0);
    const winRate = parseFloat(user.stats?.winRate || 0);
    
    const score = Math.min(100, Math.max(0, 
        (winRate * 0.5) + 
        (Math.min(pnl, 1000) / 20) + 
        (Math.min(user.stats?.totalTrades || 0, 50))
    ));

    return (
        <div className="flex items-center gap-4 p-4 hover:bg-bg-tertiary/50 rounded-lg transition-all cursor-pointer" onClick={() => onViewDetails(user)}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                rank === 1 ? 'bg-yellow-500/20 text-yellow-500' :
                rank === 2 ? 'bg-gray-400/20 text-gray-400' :
                rank === 3 ? 'bg-orange-500/20 text-orange-500' :
                'bg-bg-tertiary text-text-muted'
            }`}>
                {rank}
            </div>
            
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <p className="font-medium truncate">{user.username}</p>
                    {user.isAdmin && <Shield className="w-3 h-3 text-accent-purple" />}
                    {user.alpacaConnected && <CheckCircle className="w-3 h-3 text-accent-green" />}
                </div>
                <p className="text-xs text-text-muted">{user.stats?.totalTrades || 0} trades</p>
            </div>
            
            <div className="text-right">
                <p className={`font-semibold ${pnl >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                    {pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}
                </p>
                <p className="text-xs text-text-muted">{winRate}% win</p>
            </div>
            
            <PaeruScore score={Math.round(score)} size="small" />

            {onEmulate && (
                <button
                    onClick={(e) => { e.stopPropagation(); onEmulate(user); }}
                    className="text-xs px-2 py-1 rounded bg-accent-purple/15 text-accent-purple hover:bg-accent-purple/25 font-semibold whitespace-nowrap"
                    title="Act as this user (fully audited)"
                >
                    Emulate
                </button>
            )}

            <ChevronRight className="w-4 h-4 text-text-muted" />
        </div>
    );
};

// ============ RBAC: PERMISSION BADGE ============
const PermBadge = ({ kind }) => {
    const map = {
        costs: { label: '💰 Costs money', cls: 'bg-accent-yellow/15 text-accent-yellow' },
        admin: { label: '🛡 Admin only', cls: 'bg-accent-purple/15 text-accent-purple' },
        write: { label: '✎ Write', cls: 'bg-accent-blue/15 text-accent-blue' },
    };
    const b = map[kind];
    if (!b) return null;
    return <span className={`text-[10px] px-1.5 py-0.5 rounded ${b.cls} whitespace-nowrap`}>{b.label}</span>;
};

// Group header checkbox that shows an indeterminate state for partial selection.
const TriStateCheckbox = ({ checked, indeterminate, disabled, onChange }) => {
    const ref = React.useRef(null);
    React.useEffect(() => { if (ref.current) ref.current.indeterminate = indeterminate; }, [indeterminate]);
    return <input ref={ref} type="checkbox" checked={checked} disabled={disabled} onChange={onChange} className="rounded w-4 h-4 accent-purple-500" />;
};

// ============ RBAC: ROLE EDITOR MODAL ============
const RoleEditorModal = ({ role, permGroups, permMeta, onClose, onSave, saving }) => {
    const isNew = !!role._isNew;
    const builtin = !!role.builtin;
    const [name, setName] = useState(role.name || '');
    const [description, setDescription] = useState(role.description || '');
    const [permSet, setPermSet] = useState(() => new Set(role.permissions || []));
    const [collapsed, setCollapsed] = useState({});
    const [filter, setFilter] = useState('');

    const groupNames = Object.keys(permGroups);
    const totalPerms = useMemo(() => Object.values(permGroups).reduce((n, arr) => n + arr.length, 0), [permGroups]);

    const badgesFor = (p) => (
        <>
            {permMeta.costsMoney.has(p.id) && <PermBadge kind="costs" />}
            {permMeta.adminOnly.has(p.id) && <PermBadge kind="admin" />}
            {permMeta.write.has(p.id) && <PermBadge kind="write" />}
        </>
    );

    const togglePerm = (id) => setPermSet(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    const toggleGroup = (perms, on) => setPermSet(prev => {
        const next = new Set(prev);
        perms.forEach(p => on ? next.add(p.id) : next.delete(p.id));
        return next;
    });

    const q = filter.trim().toLowerCase();
    const matchesFilter = (p) => !q || p.label.toLowerCase().includes(q) || p.id.toLowerCase().includes(q) || (p.description || '').toLowerCase().includes(q);

    const handleSave = () => {
        if (!name.trim()) { toast.error('Role name is required'); return; }
        onSave({ id: role._id, isNew, name: name.trim(), description: description.trim(), permissions: [...permSet] });
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-bg-secondary rounded-2xl w-full max-w-3xl max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="p-5 border-b border-border-color flex items-start justify-between gap-4">
                    <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                            <KeyRound className="w-5 h-5 text-accent-purple" />
                            <h2 className="text-lg font-bold">{isNew ? 'New Role' : `Edit Role: ${role.name}`}</h2>
                            {builtin && <span className="text-[10px] px-2 py-0.5 rounded bg-bg-tertiary text-text-muted flex items-center gap-1"><Lock className="w-3 h-3" /> Built-in</span>}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Role name"
                                className="bg-bg-tertiary rounded-lg px-3 py-2 text-sm"
                            />
                            <input
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Description"
                                className="bg-bg-tertiary rounded-lg px-3 py-2 text-sm"
                            />
                        </div>
                        {builtin && <p className="text-xs text-text-muted mt-2">Built-in roles can be tweaked but not deleted. Note: the Admin role is always a super-user regardless of the boxes below.</p>}
                    </div>
                    <button onClick={onClose} className="btn-secondary p-2">✕</button>
                </div>

                {/* Toolbar */}
                <div className="px-5 py-3 border-b border-border-color flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-medium">{permSet.size} <span className="text-text-muted">/ {totalPerms} permissions</span></span>
                    <div className="relative flex-1 min-w-[180px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter permissions..." className="w-full pl-9 pr-3 py-1.5 bg-bg-tertiary rounded-lg text-sm" />
                    </div>
                    <button onClick={() => setCollapsed(Object.fromEntries(groupNames.map(g => [g, true])))} className="text-xs text-text-muted hover:text-text-primary">Collapse all</button>
                    <button onClick={() => setCollapsed({})} className="text-xs text-text-muted hover:text-text-primary">Expand all</button>
                </div>

                {/* Matrix */}
                <div className="flex-1 overflow-y-auto p-5 space-y-3">
                    {groupNames.map(group => {
                        const perms = permGroups[group].filter(matchesFilter);
                        if (!perms.length) return null;
                        const selectedInGroup = perms.filter(p => permSet.has(p.id)).length;
                        const allOn = selectedInGroup === perms.length;
                        const isCollapsed = collapsed[group];
                        return (
                            <div key={group} className="border border-border-color rounded-xl overflow-hidden">
                                <div className="flex items-center gap-3 px-4 py-3 bg-bg-tertiary/40">
                                    <TriStateCheckbox
                                        checked={allOn}
                                        indeterminate={selectedInGroup > 0 && !allOn}
                                        onChange={() => toggleGroup(perms, !allOn)}
                                    />
                                    <button onClick={() => setCollapsed(c => ({ ...c, [group]: !c[group] }))} className="flex items-center gap-2 flex-1 text-left">
                                        {isCollapsed ? <ChevronRight className="w-4 h-4 text-text-muted" /> : <ChevronDown className="w-4 h-4 text-text-muted" />}
                                        <span className="font-semibold text-sm">{group}</span>
                                    </button>
                                    <span className="text-xs text-text-muted">{selectedInGroup}/{perms.length}</span>
                                </div>
                                {!isCollapsed && (
                                    <div className="divide-y divide-border-color">
                                        {perms.map(p => (
                                            <label key={p.id} className="flex items-start gap-3 px-4 py-2.5 hover:bg-bg-tertiary/30 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={permSet.has(p.id)}
                                                    onChange={() => togglePerm(p.id)}
                                                    className="mt-0.5 rounded w-4 h-4 accent-purple-500"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="text-sm font-medium">{p.label}</span>
                                                        {badgesFor(p)}
                                                    </div>
                                                    <p className="text-xs text-text-muted">{p.description}</p>
                                                    <p className="text-[10px] text-text-muted/60 font-mono mt-0.5">{p.id}</p>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-border-color flex items-center justify-end gap-3">
                    <button onClick={onClose} className="btn-secondary">Cancel</button>
                    <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2 disabled:opacity-50">
                        {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {isNew ? 'Create Role' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ============ RBAC: ROLES PANEL ============
const RolesPanel = ({ roles, onNew, onEdit, onDelete }) => (
    <div className="space-y-4">
        <div className="flex items-center justify-between">
            <div>
                <h3 className="font-semibold flex items-center gap-2"><KeyRound className="w-5 h-5 text-accent-purple" /> Roles & Permissions</h3>
                <p className="text-sm text-text-muted mt-1">Define what each role can see and do. Assign roles to users in the Users tab.</p>
            </div>
            <button onClick={onNew} className="btn-primary flex items-center gap-2"><Plus className="w-4 h-4" /> New Role</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {roles.map(role => (
                <div key={role._id} className="card p-4 flex flex-col">
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold flex items-center gap-2">
                            {role.name}
                            {role.builtin && <span className="text-[10px] px-1.5 py-0.5 rounded bg-bg-tertiary text-text-muted flex items-center gap-1"><Lock className="w-3 h-3" /> Built-in</span>}
                        </h4>
                    </div>
                    <p className="text-xs text-text-muted flex-1 mb-3">{role.description || 'No description'}</p>
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-text-muted">{(role.permissions || []).length} permissions</span>
                        <div className="flex items-center gap-2">
                            <button onClick={() => onEdit(role)} className="btn-secondary px-2.5 py-1 text-xs flex items-center gap-1"><Edit2 className="w-3 h-3" /> Edit</button>
                            {!role.builtin && (
                                <button onClick={() => onDelete(role)} className="btn-secondary px-2.5 py-1 text-xs text-accent-red flex items-center gap-1"><Trash2 className="w-3 h-3" /></button>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

// ============ INVITES PANEL ============
const inviteLink = (token) => `${window.location.origin}/register?invite=${token}`;

const InviteStatus = ({ inv }) => {
    let label = 'Active', cls = 'bg-accent-green/15 text-accent-green';
    if (inv.revoked) { label = 'Revoked'; cls = 'bg-accent-red/15 text-accent-red'; }
    else if (inv.used) { label = 'Used'; cls = 'bg-text-muted/15 text-text-muted'; }
    else if (inv.expiresAt && Date.parse(inv.expiresAt) < Date.now()) { label = 'Expired'; cls = 'bg-accent-yellow/15 text-accent-yellow'; }
    return <span className={`text-[10px] px-2 py-0.5 rounded ${cls}`}>{label}</span>;
};

const InvitesPanel = ({ invites, roles, onCreate, onRevoke }) => {
    const [role, setRole] = useState('trader');
    const [email, setEmail] = useState('');
    const [expiresInDays, setExpiresInDays] = useState(7);
    const [creating, setCreating] = useState(false);

    const submit = async () => {
        setCreating(true);
        await onCreate({ role, email: email.trim() || null, expiresInDays: Number(expiresInDays) });
        setEmail('');
        setCreating(false);
    };

    const copy = (token) => {
        navigator.clipboard.writeText(inviteLink(token)).then(
            () => toast.success('Invite link copied'),
            () => toast.error('Copy failed — select the link manually')
        );
    };

    const isActive = (inv) => !inv.revoked && !inv.used && !(inv.expiresAt && Date.parse(inv.expiresAt) < Date.now());

    return (
        <div className="space-y-6">
            {/* Create */}
            <div className="card p-5">
                <h3 className="font-semibold flex items-center gap-2 mb-1"><Ticket className="w-5 h-5 text-accent-purple" /> Invite someone</h3>
                <p className="text-sm text-text-muted mb-4">Generates a single-use link. Registration is invite-only, so only people with a link can join.</p>
                <div className="flex flex-wrap items-end gap-3">
                    <div>
                        <label className="block text-xs text-text-muted mb-1">Role</label>
                        <select value={role} onChange={(e) => setRole(e.target.value)} className="bg-bg-tertiary rounded-lg px-3 py-2 text-sm">
                            {roles.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                            {roles.length === 0 && <option value="trader">Trader</option>}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs text-text-muted mb-1">Expires in (days)</label>
                        <input type="number" min="1" max="90" value={expiresInDays} onChange={(e) => setExpiresInDays(e.target.value)} className="bg-bg-tertiary rounded-lg px-3 py-2 text-sm w-28" />
                    </div>
                    <div className="flex-1 min-w-[180px]">
                        <label className="block text-xs text-text-muted mb-1">Email <span className="opacity-60">(optional note)</span></label>
                        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="friend@email.com" className="bg-bg-tertiary rounded-lg px-3 py-2 text-sm w-full" />
                    </div>
                    <button onClick={submit} disabled={creating} className="btn-primary flex items-center gap-2 disabled:opacity-50">
                        {creating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Generate link
                    </button>
                </div>
            </div>

            {/* List */}
            <div className="card overflow-hidden">
                <div className="p-4 border-b border-border-color"><h3 className="font-semibold">Invites ({invites.length})</h3></div>
                <div className="divide-y divide-border-color">
                    {invites.map(inv => (
                        <div key={inv._id} className="p-4 flex items-center gap-4 flex-wrap">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <InviteStatus inv={inv} />
                                    <span className="text-xs text-text-muted">role: <span className="text-text-secondary">{inv.role}</span></span>
                                    {inv.email && <span className="text-xs text-text-muted">• {inv.email}</span>}
                                </div>
                                <div className="flex items-center gap-2 font-mono text-xs text-text-muted truncate">
                                    <Link2 className="w-3 h-3 flex-none" />
                                    <span className="truncate">{inviteLink(inv._id)}</span>
                                </div>
                                <p className="text-[10px] text-text-muted/70 mt-1">
                                    created {inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : '-'}
                                    {inv.expiresAt && ` • expires ${new Date(inv.expiresAt).toLocaleDateString()}`}
                                    {inv.used && inv.usedAt && ` • used ${new Date(inv.usedAt).toLocaleDateString()}`}
                                </p>
                            </div>
                            {isActive(inv) && (
                                <div className="flex items-center gap-2">
                                    <button onClick={() => copy(inv._id)} className="btn-secondary px-3 py-1.5 text-xs flex items-center gap-1"><Copy className="w-3 h-3" /> Copy link</button>
                                    <button onClick={() => onRevoke(inv)} className="btn-secondary px-2.5 py-1.5 text-xs text-accent-red flex items-center gap-1"><Trash2 className="w-3 h-3" /></button>
                                </div>
                            )}
                        </div>
                    ))}
                    {invites.length === 0 && <div className="p-8 text-center text-text-muted">No invites yet — generate one above.</div>}
                </div>
            </div>
        </div>
    );
};

// ============ MAIN COMPONENT ============
const PlatformAdminDashboard = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [trades, setTrades] = useState([]);
    const [logs, setLogs] = useState([]);
    const [botActivity, setBotActivity] = useState([]);
    const [botData, setBotData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedUser, setSelectedUser] = useState(null);
    const [emulateTarget, setEmulateTarget] = useState(null); // admin "act as user" overlay
    const [searchQuery, setSearchQuery] = useState('');
    const [showInactive, setShowInactive] = useState(false);
    const [dateRange, setDateRange] = useState('30d');
    // RBAC
    const [roles, setRoles] = useState([]);
    const [permGroups, setPermGroups] = useState({});
    const [permMeta, setPermMeta] = useState({ costsMoney: new Set(), adminOnly: new Set(), write: new Set() });
    const [editingRole, setEditingRole] = useState(null);
    const [roleSaving, setRoleSaving] = useState(false);
    const [invites, setInvites] = useState([]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [statsRes, usersRes, tradesRes] = await Promise.all([
                adminAPI.getStats(),
                adminAPI.getUsers(showInactive),
                adminAPI.getTrades({ limit: 500 })
            ]);
            
            setStats(statsRes);
            setUsers(usersRes.users || []);
            setTrades(tradesRes.trades || []);
            
            if (activeTab === 'logs') {
                const logsRes = await adminAPI.getLogs({ limit: 100 });
                setLogs(logsRes.logs || []);
            } else if (activeTab === 'bot') {
                const botRes = await adminAPI.getBotActivity(100);
                setBotActivity(botRes.activity || []);
            } else if (activeTab === 'bots') {
                const botsRes = await adminAPI.getBots();
                setBotData(botsRes);
            }

            // Roles power the Roles tab and every user-detail role dropdown (openable from any tab).
            const rolesRes = await adminAPI.getRoles();
            setRoles(rolesRes.roles || []);
            if (activeTab === 'roles') {
                const permRes = await adminAPI.getPermissions();
                setPermGroups(permRes.groups || {});
                setPermMeta({
                    costsMoney: new Set(permRes.costsMoneyIds || []),
                    adminOnly: new Set(permRes.adminOnlyIds || []),
                    write: new Set(permRes.writeIds || []),
                });
            }
            if (activeTab === 'invites') {
                const invRes = await adminAPI.getInvites();
                setInvites(invRes.invites || []);
            }
        } catch (error) {
            console.error('Failed to fetch admin data:', error);
            toast.error('Failed to load admin data');
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, [activeTab, showInactive]);

    const analytics = useMemo(() => {
        if (!trades.length) return null;
        
        const dailyPnL = {};
        trades.forEach(t => {
            const date = t.timestamp?.split('T')[0] || 'unknown';
            if (!dailyPnL[date]) dailyPnL[date] = { date, pnl: 0, trades: 0, wins: 0 };
            dailyPnL[date].pnl += parseFloat(t.pnl) || 0;
            dailyPnL[date].trades++;
            if ((parseFloat(t.pnl) || 0) > 0) dailyPnL[date].wins++;
        });
        
        const dailyData = Object.values(dailyPnL)
            .sort((a, b) => a.date.localeCompare(b.date))
            .slice(-30);
        
        let cumulative = 0;
        const cumulativeData = dailyData.map(d => {
            cumulative += d.pnl;
            return { ...d, cumulative };
        });
        
        const wins = trades.filter(t => (parseFloat(t.pnl) || 0) > 0).length;
        const losses = trades.filter(t => (parseFloat(t.pnl) || 0) < 0).length;
        const breakeven = trades.length - wins - losses;
        
        const bySymbol = {};
        trades.forEach(t => {
            if (!bySymbol[t.symbol]) bySymbol[t.symbol] = { symbol: t.symbol, pnl: 0, count: 0 };
            bySymbol[t.symbol].pnl += parseFloat(t.pnl) || 0;
            bySymbol[t.symbol].count++;
        });
        const topSymbols = Object.values(bySymbol)
            .sort((a, b) => b.count - a.count)
            .slice(0, 5);
        
        const byHour = {};
        trades.forEach(t => {
            const hour = new Date(t.timestamp).getHours();
            if (!byHour[hour]) byHour[hour] = { hour, pnl: 0, count: 0 };
            byHour[hour].pnl += parseFloat(t.pnl) || 0;
            byHour[hour].count++;
        });
        const hourlyData = Object.values(byHour).sort((a, b) => a.hour - b.hour);
        
        const totalPnL = trades.reduce((sum, t) => sum + (parseFloat(t.pnl) || 0), 0);
        const winRate = trades.length > 0 ? (wins / trades.length) * 100 : 0;
        const profitFactor = losses > 0 ? 
            (trades.filter(t => (parseFloat(t.pnl) || 0) > 0).reduce((sum, t) => sum + parseFloat(t.pnl), 0) /
             Math.abs(trades.filter(t => (parseFloat(t.pnl) || 0) < 0).reduce((sum, t) => sum + parseFloat(t.pnl), 0))) : 0;
        
        const score = Math.min(100, Math.max(0, Math.round(
            (winRate * 0.4) +
            (Math.min(profitFactor, 3) * 15) +
            (totalPnL > 0 ? 20 : 0) +
            (trades.length > 10 ? 10 : trades.length)
        )));
        
        const avgWin = wins > 0 ? 
            trades.filter(t => (parseFloat(t.pnl) || 0) > 0).reduce((sum, t) => sum + parseFloat(t.pnl), 0) / wins : 0;
        const avgLoss = losses > 0 ?
            trades.filter(t => (parseFloat(t.pnl) || 0) < 0).reduce((sum, t) => sum + parseFloat(t.pnl), 0) / losses : 0;

        return {
            dailyData,
            cumulativeData,
            wins,
            losses,
            breakeven,
            winRate,
            topSymbols,
            hourlyData,
            score,
            totalPnL,
            profitFactor,
            avgWin,
            avgLoss,
        };
    }, [trades]);

    const handleStartBot = async (userId) => {
        try {
            const result = await adminAPI.startBot(userId);
            if (result.success) {
                toast.success('Bot started');
                fetchData();
            } else {
                toast.error(result.error || 'Failed to start bot');
            }
        } catch (error) {
            toast.error('Failed to start bot');
        }
    };

    const handleStopBot = async (userId) => {
        try {
            const result = await adminAPI.stopBot(userId);
            if (result.success) {
                toast.success('Bot stopped');
                fetchData();
            } else {
                toast.error(result.error || 'Failed to stop bot');
            }
        } catch (error) {
            toast.error('Failed to stop bot');
        }
    };

    const handleStartAllBots = async () => {
        if (!window.confirm('Start bots for all eligible users?')) return;
        try {
            const result = await adminAPI.startAllBots();
            toast.success(`Started ${result.started} bots`);
            fetchData();
        } catch (error) {
            toast.error('Failed to start all bots');
        }
    };

    const handleStopAllBots = async () => {
        if (!window.confirm('Stop ALL running bots?')) return;
        try {
            const result = await adminAPI.stopAllBots();
            toast.success(`Stopped ${result.stopped} bots`);
            fetchData();
        } catch (error) {
            toast.error('Failed to stop all bots');
        }
    };

    const handleToggleUser = async (userId) => {
        try {
            const result = await adminAPI.toggleUser(userId);
            toast.success(result.message);
            fetchData();
        } catch (error) {
            toast.error('Failed to toggle user status');
        }
    };

    const handleViewUser = async (user) => {
        try {
            const details = await adminAPI.getUserDetails(user.id || user._id);
            setSelectedUser(details);
        } catch (error) {
            toast.error('Failed to load user details');
        }
    };

    // ---- RBAC handlers ----
    const handleNewRole = () => setEditingRole({ _isNew: true, name: '', description: '', permissions: [], builtin: false });
    const handleEditRole = (role) => setEditingRole(role);

    const handleSaveRole = async ({ id, isNew, name, description, permissions }) => {
        setRoleSaving(true);
        try {
            const res = isNew
                ? await adminAPI.createRole({ name, description, permissions })
                : await adminAPI.updateRole(id, { name, description, permissions });
            if (res.success) {
                toast.success(isNew ? 'Role created' : 'Role updated');
                setEditingRole(null);
                const rolesRes = await adminAPI.getRoles();
                setRoles(rolesRes.roles || []);
            } else {
                toast.error(res.error || 'Failed to save role');
            }
        } catch (e) {
            toast.error('Failed to save role');
        }
        setRoleSaving(false);
    };

    const handleDeleteRole = async (role) => {
        if (!window.confirm(`Delete the "${role.name}" role? Users with this role will fall back to Trader.`)) return;
        try {
            const res = await adminAPI.deleteRole(role._id);
            if (res.success) {
                toast.success('Role deleted');
                setRoles(prev => prev.filter(r => r._id !== role._id));
            } else {
                toast.error(res.error || 'Failed to delete role');
            }
        } catch (e) {
            toast.error('Failed to delete role');
        }
    };

    const handleCreateInvite = async (body) => {
        try {
            const res = await adminAPI.createInvite(body);
            if (res.success) {
                toast.success('Invite created');
                setInvites(prev => [res.invite, ...prev]);
                navigator.clipboard?.writeText(inviteLink(res.invite._id)).then(
                    () => toast.success('Link copied to clipboard'),
                    () => {}
                );
            } else {
                toast.error(res.error || 'Failed to create invite');
            }
        } catch (e) {
            toast.error('Failed to create invite');
        }
    };

    const handleRevokeInvite = async (inv) => {
        if (!window.confirm('Revoke this invite? The link will stop working.')) return;
        try {
            const res = await adminAPI.revokeInvite(inv._id);
            if (res.success) {
                toast.success('Invite revoked');
                setInvites(prev => prev.map(i => i._id === inv._id ? { ...i, revoked: true } : i));
            } else {
                toast.error(res.error || 'Failed to revoke invite');
            }
        } catch (e) {
            toast.error('Failed to revoke invite');
        }
    };

    const handleAssignRole = async (userId, role) => {
        try {
            const res = await adminAPI.assignRole(userId, role);
            if (res.success) {
                toast.success('Role assigned');
                // Reflect the change in the open modal and the users list immediately.
                setSelectedUser(prev => prev && prev.user ? { ...prev, user: { ...prev.user, role, isAdmin: role === 'admin' } } : prev);
                setUsers(prev => prev.map(u => (u.id || u._id) === userId ? { ...u, role, isAdmin: role === 'admin' } : u));
            } else {
                toast.error(res.error || 'Failed to assign role');
            }
        } catch (e) {
            toast.error('Failed to assign role');
        }
    };

    const filteredUsers = users.filter(u => 
        u.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (u.email && u.email.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const rankedUsers = [...users].sort((a, b) => 
        parseFloat(b.stats?.totalPnL || 0) - parseFloat(a.stats?.totalPnL || 0)
    );

    return (
        <div className="min-h-screen bg-bg-primary">
            <Navigation onRefresh={fetchData} refreshing={loading} />
            
            <div className="max-w-[1800px] mx-auto px-4 sm:px-6 py-6 pb-24 md:pb-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-3">
                            <Shield className="w-7 h-7 text-accent-purple" />
                            Platform Analytics
                        </h1>
                        <p className="text-text-muted mt-1">Performance insights & user management</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <select 
                            value={dateRange}
                            onChange={(e) => setDateRange(e.target.value)}
                            className="bg-bg-tertiary rounded-lg px-3 py-2 text-sm"
                        >
                            <option value="7d">Last 7 days</option>
                            <option value="30d">Last 30 days</option>
                            <option value="90d">Last 90 days</option>
                            <option value="all">All time</option>
                        </select>
                        <button 
                            onClick={fetchData} 
                            disabled={loading}
                            className="btn-secondary flex items-center gap-2"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                            Refresh
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                    {[
                        { id: 'overview', label: 'Overview', icon: BarChart3 },
                        { id: 'analytics', label: 'Analytics', icon: PieChart },
                        { id: 'users', label: 'Users', icon: Users },
                        { id: 'roles', label: 'Roles', icon: KeyRound },
                        { id: 'invites', label: 'Invites', icon: Ticket },
                        { id: 'bots', label: 'Bot Control', icon: Bot },
                        { id: 'trades', label: 'Trades', icon: Activity },
                        { id: 'logs', label: 'Logs', icon: Database },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                                activeTab === tab.id
                                    ? 'bg-accent-purple text-white'
                                    : 'bg-bg-tertiary text-text-secondary hover:bg-bg-card'
                            }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Overview Tab */}
                {activeTab === 'overview' && stats && (
                    <div className="space-y-6">
                        {/* Top Stats Row */}
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                            <StatCard
                                title="Total Users"
                                value={stats.users?.total || 0}
                                subtitle={`${stats.users?.withAlpaca || 0} with Alpaca`}
                                icon={Users}
                                color="blue"
                            />
                            <StatCard
                                title="Bots Active"
                                value={stats.bots?.running || 0}
                                icon={Bot}
                                color="purple"
                            />
                            <StatCard
                                title="Total Trades"
                                value={stats.trades?.total || 0}
                                subtitle={`${stats.trades?.today || 0} today`}
                                icon={Activity}
                                color="cyan"
                            />
                            <StatCard
                                title="Win Rate"
                                value={analytics ? `${analytics.winRate.toFixed(1)}%` : '0%'}
                                icon={Target}
                                color={analytics?.winRate >= 50 ? 'green' : 'red'}
                            />
                            <StatCard
                                title="Profit Factor"
                                value={analytics ? analytics.profitFactor.toFixed(2) : '0'}
                                icon={TrendingUp}
                                color={analytics?.profitFactor >= 1 ? 'green' : 'red'}
                            />
                            <StatCard
                                title="Net P&L"
                                value={analytics ? `$${analytics.totalPnL.toFixed(2)}` : '$0'}
                                icon={DollarSign}
                                color={analytics?.totalPnL >= 0 ? 'green' : 'red'}
                            />
                        </div>

                        {/* Main Charts Row */}
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                            {/* Paeru Score */}
                            <div className="card p-6 flex flex-col items-center justify-center">
                                <h3 className="font-semibold mb-4 flex items-center gap-2">
                                    <Award className="w-5 h-5 text-accent-yellow" />
                                    Paeru Score
                                </h3>
                                <PaeruScore score={analytics?.score || 0} size="large" />
                                <div className="grid grid-cols-3 gap-4 mt-6 w-full text-center">
                                    <div>
                                        <p className="text-lg font-bold text-accent-green">{analytics?.wins || 0}</p>
                                        <p className="text-xs text-text-muted">Wins</p>
                                    </div>
                                    <div>
                                        <p className="text-lg font-bold text-accent-red">{analytics?.losses || 0}</p>
                                        <p className="text-xs text-text-muted">Losses</p>
                                    </div>
                                    <div>
                                        <p className="text-lg font-bold text-text-muted">{analytics?.breakeven || 0}</p>
                                        <p className="text-xs text-text-muted">B/E</p>
                                    </div>
                                </div>
                            </div>

                            {/* Cumulative P&L Chart */}
                            <div className="card p-4 lg:col-span-2">
                                <h3 className="font-semibold mb-4 flex items-center gap-2">
                                    <TrendingUp className="w-5 h-5 text-accent-green" />
                                    Cumulative P&L
                                </h3>
                                <div className="h-64">
                                    {analytics?.cumulativeData?.length > 0 ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={analytics.cumulativeData}>
                                                <defs>
                                                    <linearGradient id="colorPnl" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor={COLORS.green} stopOpacity={0.3}/>
                                                        <stop offset="95%" stopColor={COLORS.green} stopOpacity={0}/>
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                                <XAxis 
                                                    dataKey="date" 
                                                    stroke="#6b7280" 
                                                    fontSize={12}
                                                    tickFormatter={(v) => v.slice(5)}
                                                />
                                                <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `$${v}`} />
                                                <Tooltip 
                                                    contentStyle={{ background: '#1f2937', border: 'none', borderRadius: '8px' }}
                                                    formatter={(value) => [`$${value.toFixed(2)}`, 'Cumulative P&L']}
                                                />
                                                <Area 
                                                    type="monotone" 
                                                    dataKey="cumulative" 
                                                    stroke={COLORS.green}
                                                    fill="url(#colorPnl)"
                                                    strokeWidth={2}
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="h-full flex items-center justify-center text-text-muted">
                                            No trade data available
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Second Row */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Daily P&L Bar Chart */}
                            <div className="card p-4">
                                <h3 className="font-semibold mb-4 flex items-center gap-2">
                                    <BarChart3 className="w-5 h-5 text-accent-blue" />
                                    Daily Net P&L
                                </h3>
                                <div className="h-64">
                                    {analytics?.dailyData?.length > 0 ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={analytics.dailyData}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                                <XAxis 
                                                    dataKey="date" 
                                                    stroke="#6b7280" 
                                                    fontSize={12}
                                                    tickFormatter={(v) => v.slice(5)}
                                                />
                                                <YAxis stroke="#6b7280" fontSize={12} tickFormatter={(v) => `$${v}`} />
                                                <Tooltip 
                                                    contentStyle={{ background: '#1f2937', border: 'none', borderRadius: '8px' }}
                                                    formatter={(value) => [`$${value.toFixed(2)}`, 'P&L']}
                                                />
                                                <Bar 
                                                    dataKey="pnl" 
                                                    radius={[4, 4, 0, 0]}
                                                    fill={COLORS.blue}
                                                >
                                                    {analytics.dailyData.map((entry, index) => (
                                                        <Cell 
                                                            key={`cell-${index}`} 
                                                            fill={entry.pnl >= 0 ? COLORS.green : COLORS.red} 
                                                        />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="h-full flex items-center justify-center text-text-muted">
                                            No trade data available
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Calendar Heatmap */}
                            <CalendarHeatmap trades={trades} />
                        </div>

                        {/* Third Row - Leaderboard */}
                        <div className="card overflow-hidden">
                            <div className="p-4 border-b border-border-color flex items-center justify-between">
                                <h3 className="font-semibold flex items-center gap-2">
                                    <Trophy className="w-5 h-5 text-accent-yellow" />
                                    User Leaderboard
                                </h3>
                                <button 
                                    onClick={() => setActiveTab('users')}
                                    className="text-sm text-accent-blue hover:underline"
                                >
                                    View all users →
                                </button>
                            </div>
                            <div className="divide-y divide-border-color">
                                {rankedUsers.slice(0, 5).map((user, index) => (
                                    <UserPerformanceRow 
                                        key={user.id || user._id} 
                                        user={user} 
                                        rank={index + 1}
                                        onViewDetails={handleViewUser}
                                    />
                                ))}
                                {rankedUsers.length === 0 && (
                                    <div className="p-8 text-center text-text-muted">
                                        No users found
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Analytics Tab */}
                {activeTab === 'analytics' && (
                    <div className="space-y-6">
                        {/* Key Metrics */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="card p-4 text-center">
                                <p className="text-text-muted text-sm mb-1">Avg Winning Trade</p>
                                <p className="text-2xl font-bold text-accent-green">
                                    +${(analytics?.avgWin || 0).toFixed(2)}
                                </p>
                            </div>
                            <div className="card p-4 text-center">
                                <p className="text-text-muted text-sm mb-1">Avg Losing Trade</p>
                                <p className="text-2xl font-bold text-accent-red">
                                    ${(analytics?.avgLoss || 0).toFixed(2)}
                                </p>
                            </div>
                            <div className="card p-4 text-center">
                                <p className="text-text-muted text-sm mb-1">Win/Loss Ratio</p>
                                <p className="text-2xl font-bold">
                                    {analytics?.losses > 0 ? (analytics.wins / analytics.losses).toFixed(2) : '∞'}
                                </p>
                            </div>
                            <div className="card p-4 text-center">
                                <p className="text-text-muted text-sm mb-1">Expectancy</p>
                                <p className={`text-2xl font-bold ${
                                    analytics && ((analytics.avgWin * analytics.winRate/100 + analytics.avgLoss * (1 - analytics.winRate/100)) >= 0)
                                    ? 'text-accent-green' : 'text-accent-red'
                                }`}>
                                    ${analytics ? ((analytics.avgWin * analytics.winRate/100) + (analytics.avgLoss * (1 - analytics.winRate/100))).toFixed(2) : '0.00'}
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Win/Loss Pie Chart */}
                            <div className="card p-4">
                                <h3 className="font-semibold mb-4 flex items-center gap-2">
                                    <PieChart className="w-5 h-5 text-accent-purple" />
                                    Trade Outcomes
                                </h3>
                                <div className="h-64">
                                    {analytics && (analytics.wins > 0 || analytics.losses > 0) ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <RechartsPie>
                                                <Pie
                                                    data={[
                                                        { name: 'Wins', value: analytics.wins },
                                                        { name: 'Losses', value: analytics.losses },
                                                        { name: 'Breakeven', value: analytics.breakeven },
                                                    ]}
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={60}
                                                    outerRadius={100}
                                                    paddingAngle={2}
                                                    dataKey="value"
                                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                >
                                                    <Cell fill={COLORS.green} />
                                                    <Cell fill={COLORS.red} />
                                                    <Cell fill={COLORS.gray} />
                                                </Pie>
                                                <Tooltip />
                                            </RechartsPie>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="h-full flex items-center justify-center text-text-muted">
                                            No trade data available
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Top Symbols */}
                            <div className="card p-4">
                                <h3 className="font-semibold mb-4 flex items-center gap-2">
                                    <Target className="w-5 h-5 text-accent-cyan" />
                                    Top Traded Symbols
                                </h3>
                                <div className="h-64">
                                    {analytics?.topSymbols?.length > 0 ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={analytics.topSymbols} layout="vertical">
                                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                                <XAxis type="number" stroke="#6b7280" fontSize={12} />
                                                <YAxis dataKey="symbol" type="category" stroke="#6b7280" fontSize={12} width={60} />
                                                <Tooltip 
                                                    contentStyle={{ background: '#1f2937', border: 'none', borderRadius: '8px' }}
                                                    formatter={(value, name) => [
                                                        name === 'count' ? `${value} trades` : `$${value.toFixed(2)}`,
                                                        name === 'count' ? 'Trades' : 'P&L'
                                                    ]}
                                                />
                                                <Bar dataKey="count" fill={COLORS.blue} radius={[0, 4, 4, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="h-full flex items-center justify-center text-text-muted">
                                            No trade data available
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Trade Time Distribution */}
                            <div className="card p-4 lg:col-span-2">
                                <h3 className="font-semibold mb-4 flex items-center gap-2">
                                    <Clock className="w-5 h-5 text-accent-yellow" />
                                    Performance by Hour
                                </h3>
                                <div className="h-64">
                                    {analytics?.hourlyData?.length > 0 ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <ComposedChart data={analytics.hourlyData}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                                                <XAxis 
                                                    dataKey="hour" 
                                                    stroke="#6b7280" 
                                                    fontSize={12}
                                                    tickFormatter={(h) => `${h}:00`}
                                                />
                                                <YAxis yAxisId="left" stroke="#6b7280" fontSize={12} />
                                                <YAxis yAxisId="right" orientation="right" stroke="#6b7280" fontSize={12} />
                                                <Tooltip 
                                                    contentStyle={{ background: '#1f2937', border: 'none', borderRadius: '8px' }}
                                                    formatter={(value, name) => [
                                                        name === 'count' ? value : `$${value.toFixed(2)}`,
                                                        name === 'count' ? 'Trades' : 'P&L'
                                                    ]}
                                                />
                                                <Bar yAxisId="left" dataKey="count" fill={COLORS.blue} opacity={0.5} radius={[4, 4, 0, 0]} />
                                                <Line yAxisId="right" type="monotone" dataKey="pnl" stroke={COLORS.green} strokeWidth={2} dot />
                                            </ComposedChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="h-full flex items-center justify-center text-text-muted">
                                            No trade data available
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Users Tab */}
                {activeTab === 'users' && (
                    <div className="card overflow-hidden">
                        <div className="p-4 border-b border-border-color flex flex-wrap gap-4 items-center">
                            <div className="relative flex-1 min-w-[200px]">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                                <input
                                    type="text"
                                    placeholder="Search users..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 bg-bg-tertiary rounded-lg"
                                />
                            </div>
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={showInactive}
                                    onChange={(e) => setShowInactive(e.target.checked)}
                                    className="rounded"
                                />
                                Show inactive
                            </label>
                        </div>

                        <div className="divide-y divide-border-color">
                            {filteredUsers.map((user, index) => (
                                <UserPerformanceRow
                                    key={user.id || user._id}
                                    user={user}
                                    rank={rankedUsers.findIndex(u => (u.id || u._id) === (user.id || user._id)) + 1}
                                    onViewDetails={handleViewUser}
                                    onEmulate={setEmulateTarget}
                                />
                            ))}
                        </div>

                        {filteredUsers.length === 0 && (
                            <div className="p-8 text-center text-text-muted">
                                No users found
                            </div>
                        )}
                    </div>
                )}

                {/* Roles Tab */}
                {activeTab === 'roles' && (
                    <RolesPanel
                        roles={roles}
                        onNew={handleNewRole}
                        onEdit={handleEditRole}
                        onDelete={handleDeleteRole}
                    />
                )}

                {/* Invites Tab */}
                {activeTab === 'invites' && (
                    <InvitesPanel
                        invites={invites}
                        roles={roles}
                        onCreate={handleCreateInvite}
                        onRevoke={handleRevokeInvite}
                    />
                )}

                {/* Bot Control Tab */}
                {activeTab === 'bots' && (
                    <div className="space-y-6">
                        <div className="card p-4">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-semibold flex items-center gap-2">
                                    <Bot className="w-5 h-5" />
                                    Bulk Bot Control
                                </h3>
                                <div className="flex gap-2">
                                    <button onClick={handleStartAllBots} className="btn-primary flex items-center gap-2">
                                        <Play className="w-4 h-4" />
                                        Start All
                                    </button>
                                    <button onClick={handleStopAllBots} className="btn-secondary text-accent-red flex items-center gap-2">
                                        <Square className="w-4 h-4" />
                                        Stop All
                                    </button>
                                </div>
                            </div>
                            <p className="text-sm text-text-muted">
                                Only users with Alpaca credentials can have bots started. You have 3 Alpaca paper accounts available.
                            </p>
                        </div>

                        <div className="card overflow-hidden">
                            <div className="p-4 border-b border-border-color">
                                <h3 className="font-semibold">
                                    Eligible Users ({botData?.eligibleUsers?.length || 0})
                                </h3>
                            </div>
                            <div className="divide-y divide-border-color">
                                {botData?.eligibleUsers?.map(user => (
                                    <div key={user.userId} className="p-4 flex items-center justify-between">
                                        <div className="flex items-center gap-4">
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                                user.botEnabled ? 'bg-accent-green/20' : 'bg-bg-tertiary'
                                            }`}>
                                                <Bot className={`w-5 h-5 ${user.botEnabled ? 'text-accent-green' : 'text-text-muted'}`} />
                                            </div>
                                            <div>
                                                <p className="font-medium">{user.username}</p>
                                                <p className="text-xs text-text-muted">
                                                    {user.botConfig?.strategy || 'Default'} • {user.botConfig?.riskLevel || 'medium'} risk
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {user.botEnabled ? (
                                                <>
                                                    <span className="flex items-center gap-2 text-accent-green text-sm">
                                                        <span className="w-2 h-2 rounded-full bg-accent-green animate-pulse" />
                                                        Running
                                                    </span>
                                                    <button 
                                                        onClick={() => handleStopBot(user.userId)}
                                                        className="btn-secondary text-accent-red px-3 py-1"
                                                    >
                                                        Stop
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="text-text-muted text-sm">Stopped</span>
                                                    <button 
                                                        onClick={() => handleStartBot(user.userId)}
                                                        className="btn-primary px-3 py-1"
                                                    >
                                                        Start
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                {(!botData?.eligibleUsers || botData.eligibleUsers.length === 0) && (
                                    <div className="p-8 text-center text-text-muted">
                                        No users with Alpaca credentials
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Trades Tab */}
                {activeTab === 'trades' && (
                    <div className="card overflow-hidden">
                        <div className="p-4 border-b border-border-color flex items-center justify-between">
                            <h3 className="font-semibold">Recent Trades ({trades.length})</h3>
                            <button className="btn-secondary text-sm flex items-center gap-2">
                                <Download className="w-4 h-4" />
                                Export CSV
                            </button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-bg-tertiary">
                                    <tr>
                                        <th className="text-left py-3 px-4 text-sm text-text-muted font-medium">Time</th>
                                        <th className="text-left py-3 px-4 text-sm text-text-muted font-medium">User</th>
                                        <th className="text-left py-3 px-4 text-sm text-text-muted font-medium">Symbol</th>
                                        <th className="text-left py-3 px-4 text-sm text-text-muted font-medium">Side</th>
                                        <th className="text-left py-3 px-4 text-sm text-text-muted font-medium">Qty</th>
                                        <th className="text-left py-3 px-4 text-sm text-text-muted font-medium">Price</th>
                                        <th className="text-left py-3 px-4 text-sm text-text-muted font-medium">P&L</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {trades.slice(0, 100).map(trade => (
                                        <tr key={trade._id} className="border-b border-border-color hover:bg-bg-tertiary/50">
                                            <td className="py-3 px-4 text-sm text-text-muted">
                                                {trade.timestamp ? new Date(trade.timestamp).toLocaleString() : '-'}
                                            </td>
                                            <td className="py-3 px-4">{trade.username}</td>
                                            <td className="py-3 px-4 font-mono font-medium">{trade.symbol}</td>
                                            <td className="py-3 px-4">
                                                <span className={`px-2 py-1 rounded text-xs ${
                                                    trade.side === 'buy' ? 'bg-accent-green/15 text-accent-green' : 'bg-accent-red/15 text-accent-red'
                                                }`}>
                                                    {trade.side?.toUpperCase()}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4">{trade.quantity || trade.qty}</td>
                                            <td className="py-3 px-4">${parseFloat(trade.price || 0).toFixed(2)}</td>
                                            <td className="py-3 px-4">
                                                <span className={`${parseFloat(trade.pnl || 0) >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                                                    {trade.pnl ? `${parseFloat(trade.pnl) >= 0 ? '+' : ''}$${parseFloat(trade.pnl).toFixed(2)}` : '-'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {trades.length === 0 && (
                            <div className="p-8 text-center text-text-muted">
                                No trades found
                            </div>
                        )}
                    </div>
                )}

                {/* Logs Tab */}
                {activeTab === 'logs' && (
                    <div className="card overflow-hidden">
                        <div className="p-4 border-b border-border-color">
                            <h3 className="font-semibold">System Logs</h3>
                        </div>
                        <div className="divide-y divide-border-color max-h-[600px] overflow-y-auto font-mono text-sm">
                            {logs.map(log => (
                                <div key={log._id} className="p-3 hover:bg-bg-tertiary/50">
                                    <div className="flex items-start gap-3">
                                        <span className={`px-2 py-0.5 rounded text-xs shrink-0 ${
                                            log.level === 'error' ? 'bg-accent-red/15 text-accent-red' :
                                            log.level === 'warn' ? 'bg-accent-yellow/15 text-accent-yellow' :
                                            'bg-accent-blue/15 text-accent-blue'
                                        }`}>
                                            {log.level?.toUpperCase()}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-text-primary">{log.message}</p>
                                            <p className="text-xs text-text-muted mt-1">
                                                {log.category && `[${log.category}] `}
                                                {log.timestamp ? new Date(log.timestamp).toLocaleString() : '-'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {logs.length === 0 && (
                                <div className="p-8 text-center text-text-muted">
                                    No logs found
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Role Editor Modal */}
            {editingRole && (
                <RoleEditorModal
                    role={editingRole}
                    permGroups={permGroups}
                    permMeta={permMeta}
                    saving={roleSaving}
                    onClose={() => setEditingRole(null)}
                    onSave={handleSaveRole}
                />
            )}

            {/* User Detail Modal */}
            {emulateTarget && (
                <AdminEmulate targetUser={emulateTarget} onExit={() => setEmulateTarget(null)} />
            )}

            {selectedUser && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedUser(null)}>
                    <div className="bg-bg-secondary rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b border-border-color flex items-center justify-between">
                            <h2 className="text-xl font-bold flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-accent-purple/20 flex items-center justify-center">
                                    {selectedUser.user?.username?.charAt(0).toUpperCase()}
                                </div>
                                {selectedUser.user?.username}
                            </h2>
                            <button onClick={() => setSelectedUser(null)} className="btn-secondary p-2">✕</button>
                        </div>
                        <div className="p-6 space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-text-muted text-sm">Email</p>
                                    <p>{selectedUser.user?.email || 'Not set'}</p>
                                </div>
                                <div>
                                    <p className="text-text-muted text-sm">Joined</p>
                                    <p>{selectedUser.user?.createdAt ? new Date(selectedUser.user.createdAt).toLocaleDateString() : '-'}</p>
                                </div>
                                <div>
                                    <p className="text-text-muted text-sm mb-1">Role</p>
                                    <select
                                        value={selectedUser.user?.isAdmin ? 'admin' : (selectedUser.user?.role || 'trader')}
                                        onChange={(e) => handleAssignRole(selectedUser.user?._id || selectedUser.user?.id, e.target.value)}
                                        className="bg-bg-tertiary rounded-lg px-3 py-1.5 text-sm w-full"
                                    >
                                        {roles.map(r => (
                                            <option key={r._id} value={r._id}>{r.name}</option>
                                        ))}
                                        {roles.length === 0 && <option value="trader">Trader</option>}
                                    </select>
                                </div>
                                <div>
                                    <p className="text-text-muted text-sm">Alpaca</p>
                                    <p>{selectedUser.profile?.alpacaConnected ? '✅ Connected' : '❌ Not Connected'}</p>
                                </div>
                            </div>

                            {selectedUser.profile?.botConfig && (
                                <div>
                                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                                        <Bot className="w-4 h-4" />
                                        Bot Configuration
                                    </h3>
                                    <div className="bg-bg-tertiary rounded-lg p-4 grid grid-cols-2 gap-3 text-sm">
                                        <div><span className="text-text-muted">Strategy:</span> {selectedUser.profile.botConfig.strategy}</div>
                                        <div><span className="text-text-muted">Risk:</span> {selectedUser.profile.botConfig.riskLevel}</div>
                                        <div><span className="text-text-muted">Max Position:</span> ${selectedUser.profile.botConfig.maxPositionSize}</div>
                                        <div><span className="text-text-muted">Max Daily Trades:</span> {selectedUser.profile.botConfig.maxDailyTrades}</div>
                                        <div><span className="text-text-muted">Stop Loss:</span> {selectedUser.profile.botConfig.stopLossPercent}%</div>
                                        <div><span className="text-text-muted">Take Profit:</span> {selectedUser.profile.botConfig.takeProfitPercent}%</div>
                                    </div>
                                </div>
                            )}

                            {selectedUser.recentTrades?.length > 0 && (
                                <div>
                                    <h3 className="font-semibold mb-3">Recent Trades</h3>
                                    <div className="space-y-2 max-h-60 overflow-y-auto">
                                        {selectedUser.recentTrades.slice(0, 10).map(trade => (
                                            <div key={trade._id} className="flex items-center justify-between bg-bg-tertiary rounded-lg p-3">
                                                <div>
                                                    <span className="font-mono font-medium">{trade.symbol}</span>
                                                    <span className={`ml-2 text-xs ${trade.side === 'buy' ? 'text-accent-green' : 'text-accent-red'}`}>
                                                        {trade.side?.toUpperCase()}
                                                    </span>
                                                </div>
                                                <div className="text-right">
                                                    <span className={`${parseFloat(trade.pnl || 0) >= 0 ? 'text-accent-green' : 'text-accent-red'}`}>
                                                        {trade.pnl ? `${parseFloat(trade.pnl) >= 0 ? '+' : ''}$${parseFloat(trade.pnl).toFixed(2)}` : '-'}
                                                    </span>
                                                    <p className="text-xs text-text-muted">
                                                        {trade.timestamp ? new Date(trade.timestamp).toLocaleDateString() : '-'}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PlatformAdminDashboard;
